import { useStation } from '@app/core/stations/hooks/useStation';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { isSocialExportEnabledForStation } from '@app/modules/social-export/hooks/useSocialExportEnabledForStation';
import { webApi } from '@mi-front/api-clients';

const ALLOW_ALL_PLATFORMS = true;

export interface AvailableSocialExportPlatformsResult {
  readonly platforms: SocialPlatform[];
  /**
   * False while the station social-connections request is in flight (when {@link stationId} is set).
   * Used to avoid treating an empty connection list as "no platforms" before the response arrives.
   */
  readonly areConnectionsResolved: boolean;
}

/**
 * Connected platforms for a station that are enabled for social export (fragment and composition publish).
 * When {@link ALLOW_ALL_PLATFORMS} is false, excludes platforms that already have uploads for the entity.
 */
export function useAvailableSocialExportPlatformsForStation<TUpload = unknown>(
  stationId: string | undefined,
  uploadsByPlatform: Map<string, TUpload[]> | undefined,
): AvailableSocialExportPlatformsResult {
  const station = useStation(stationId ?? '');
  const connectionsQuery = webApi.useGetSocialConnectionsConnectionsForStation(
    stationId ?? '',
    undefined,
    {
      query: { enabled: !!stationId },
    },
  );

  const results = connectionsQuery.data?.results ?? [];
  const platforms = socialPlatforms.all();

  const availablePlatforms = platforms.filter((platform) => {
    if (!isSocialExportEnabledForStation(station, platform)) return false;
    const connected =
      results.find((r) => r.platform.toUpperCase() === platform.id.toUpperCase())?.connected ??
      false;
    if (!connected) return false;
    if (ALLOW_ALL_PLATFORMS) return true;
    const uploads = uploadsByPlatform?.get(platform.id) ?? [];
    return uploads.length === 0;
  });

  const areConnectionsResolved = !stationId || !connectionsQuery.isLoading;

  return { platforms: availablePlatforms, areConnectionsResolved };
}
